import React, { useEffect, useState, useRef } from 'react';
import { getAuth, updateProfile } from 'firebase/auth';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { db } from '../firebase';
import { updateDoc, doc, collection, query, where, orderBy, getDocs, deleteDoc, getDoc, addDoc, serverTimestamp, onSnapshot, arrayUnion, arrayRemove, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FaPlus, FaEdit, FaUserPlus, FaUserCheck, FaTrash, FaBell, FaEnvelope, FaShare } from 'react-icons/fa';
import { FiMoreHorizontal, FiMoreVertical } from 'react-icons/fi';
import MovieItem from '../components/MovieItem';
import StatusPostItem from '../components/StatusPostItem';
import MusicPlayer from '../components/MusicPlayer';
import Spinner from '../components/Spinner';
import defaultCover from '../assets/svg/images/default-cover.png';
import defaultAvatar from '../assets/svg/images/default-avatar.jpg';
import { useStatusContext } from '../utils/StatusContext';
import { useFollow } from '../utils/FollowContext';
import VerificationBadge from '../components/VerificationBadge';

// CSS for hover effects, grid layout, and new "More" button dropdowns
const styles = `
  .movie-item-wrapper {
    position: relative;
    transition: transform 0.2s;
  }
  
  .movie-item-wrapper:hover {
    transform: scale(1.03);
    z-index: 10;
  }
  
  .movie-item-actions {
    position: absolute;
    top: 8px;
    right: 8px;
    display: flex;
    gap: 8px;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  .movie-item-wrapper:hover .movie-item-actions {
    opacity: 1;
  }
  
  .movie-item-actions button {
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease;
  }
  
  .movie-item-actions button:hover {
    background: rgba(0, 0, 0, 0.9);
  }
  
  /* 7-column grid layout for desktop */
  @media (min-width: 1024px) {
    .movie-grid {
      grid-template-columns: repeat(7, minmax(169px, 1fr)) !important;
    }
  }

  /* Styles for "More" button and dropdown */
  .more-button {
    background: rgba(128, 128, 128, 0.5);
    color: white;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease;
  }

  .more-button:hover {
    background: rgba(128, 128, 128, 0.7);
  }

  .dropdown-menu {
    position: absolute;
    z-index: 20;
    background: #1a1a1a;
    border: 1px solid #333;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
    min-width: 160px;
  }

  .dropdown-menu button {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 12px;
    background: none;
    border: none;
    color: white;
    text-align: left;
    font-size: 14px;
    transition: background 0.2s ease;
  }

  .dropdown-menu button:hover {
    background: #333;
  }

  .dropdown-menu button.disabled {
    color: #666;
    cursor: not-allowed;
  }

  .dropdown-menu button.disabled:hover {
    background: none;
  }

  /* Sub-tab styles */
  .sub-tab {
    padding: 8px 16px;
    font-size: 14px;
    font-weight: 500;
    color: #aaa;
    border-bottom: 2px solid transparent;
    transition: all 0.3s ease;
  }

  .sub-tab:hover {
    color: #fff;
    border-bottom: 2px solid #666;
  }

  .sub-tab.active {
    color: #3b82f6;
    border-bottom: 2px solid #3b82f6;
  }

  /* Status item wrapper */
  .status-item-wrapper {
    position: relative;
  }

  .status-item-actions {
    position: absolute;
    top: 16px;
    right: 16px;
    display: flex;
    gap: 8px;
  }

  .status-item-actions button {
    background: rgba(0, 0, 0, 0.7);
    color: white;
    border: none;
    border-radius: 50%;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.3s ease;
  }

  .status-item-actions button:hover {
    background: rgba(0, 0, 0, 0.9);
  }

  /* Status grid layout */
  .status-grid {
    display: grid;
    gap: 1rem;
  }

  /* Sticky tabs */
  .sticky-tabs {
    position: sticky;
    top: 0;
    z-index: 10;
    background: black;
  }

  /* Action buttons container */
  .action-buttons {
    display: flex;
    gap: 1rem;
    align-items: center;
  }

  /* Clickable follower count */
  .clickable-count {
    cursor: pointer;
    transition: color 0.2s ease;
  }

  .clickable-count:hover {
    color: #3b82f6;
    text-decoration: underline;
  }
`;

// Inject styles
const styleSheet = document.createElement("style");
styleSheet.type = "text/css";
styleSheet.innerText = styles;
document.head.appendChild(styleSheet);

export default function Profile() {
  const auth = getAuth();
  const navigate = useNavigate();
  const { userId } = useParams();
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [activeTab, setActiveTab] = useState('status');
  const [subTab, setSubTab] = useState('movies');
  const [changeDetail, setChangeDetail] = useState(false);
  const [content, setContent] = useState({
    movies: [],
    status: [],
    music: [],
    favorites: { movies: [], status: [] },
    liked: { movies: [], status: [] }
  });
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState({
    name: '',
    username: '',
    bio: 'No bio yet',
    followers: 0,
    following: 0,
    userCategory: 'Standard User',
    coverImage: defaultCover,
    profileImage: defaultAvatar,
    notificationSubscribers: [],
    verified: false // Add verified field to userData state
  });
  const [showCreateOptions, setShowCreateOptions] = useState(false);
  const [showProfileOptions, setShowProfileOptions] = useState(false);
  const [showCoverOptions, setShowCoverOptions] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    bio: 'No bio yet'
  });
  const [usernameAvailability, setUsernameAvailability] = useState(null);
  const [isUsernameChecking, setIsUsernameChecking] = useState(false);
  const [isImageUpdating, setIsImageUpdating] = useState({ profile: false, cover: false });
  const [activeStatusId, setActiveStatusId] = useState(null);
  const [isFollowLoading, setIsFollowLoading] = useState(false);
  const [currentUserData, setCurrentUserData] = useState(null);
  const [isNotifyLoading, setIsNotifyLoading] = useState(false);
  const [isSubscribedToNotifications, setIsSubscribedToNotifications] = useState(false);

  const createOptionsRef = useRef(null);
  const profileImageInputRef = useRef(null);
  const coverImageInputRef = useRef(null);
  const profileOptionsRef = useRef(null);
  const coverOptionsRef = useRef(null);
  const { markStatusAsViewed } = useStatusContext();
  const {
    isFollowing,
    shouldFollowBack,
    toggleFollow,
    followers,
    following,
    isInitialized
  } = useFollow();

  // Close create options when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (createOptionsRef.current && !createOptionsRef.current.contains(event.target)) {
        setShowCreateOptions(false);
      }
      if (profileOptionsRef.current && !profileOptionsRef.current.contains(event.target)) {
        setShowProfileOptions(false);
      }
      if (coverOptionsRef.current && !coverOptionsRef.current.contains(event.target)) {
        setShowCoverOptions(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Check if viewing own profile
  useEffect(() => {
    if (userId) {
      setIsCurrentUser(userId === auth.currentUser?.uid);
    } else {
      setIsCurrentUser(true);
    }
  }, [userId, auth.currentUser]);

  // Fetch user data and listen for notification subscribers
  useEffect(() => {
    async function fetchUserData() {
      const uid = userId || auth.currentUser?.uid;
      if (!uid) return;

      try {
        const userRef = doc(db, 'users', uid);
        const unsubscribe = onSnapshot(userRef, (userDoc) => {
          if (userDoc.exists()) {
            const data = userDoc.data();
            
            // Handle followers count - works for both array and object formats
            let followersCount = 0;
            if (Array.isArray(data.followers)) {
              followersCount = data.followers.length;
            } else if (data.followers && typeof data.followers === 'object') {
              followersCount = Object.keys(data.followers).length;
            }

            // Handle following count - works for both array and object formats
            let followingCount = 0;
            if (Array.isArray(data.following)) {
              followingCount = data.following.length;
            } else if (data.following && typeof data.following === 'object') {
              followingCount = Object.keys(data.following).length;
            }

            // Ensure notificationSubscribers is an array
            const notificationSubscribers = Array.isArray(data.notificationSubscribers) ? data.notificationSubscribers : [];

            // Use FollowContext for current user, Firestore for others
            if (isCurrentUser) {
              followersCount = followers.size;
              followingCount = following.size;
            }

            setUserData({
              name: data.name || '',
              username: data.username || '',
              bio: data.bio || 'No bio yet',
              followers: followersCount,
              following: followingCount,
              userCategory: data.userCategory || 'Standard User',
              coverImage: data.coverImage || defaultCover,
              profileImage: data.photoURL || defaultAvatar,
              notificationSubscribers: notificationSubscribers,
              verified: data.verified || false // Fetch verified status from Firestore
            });

            setFormData({
              name: data.name || '',
              username: data.username || '',
              bio: data.bio || 'No bio yet'
            });

            // Check if current user is subscribed to notifications
            if (auth.currentUser && !isCurrentUser) {
              setIsSubscribedToNotifications(notificationSubscribers.includes(auth.currentUser.uid));
            }
          }
        }, (error) => {
          toast.error('Failed to load user data');
          console.error('Error listening to user data:', error);
        });

        return () => unsubscribe();
      } catch (error) {
        toast.error('Failed to load user data');
      }
    }
    fetchUserData();
  }, [userId, auth.currentUser, isCurrentUser, followers, following]);

  // Fetch current user's data for notification sender name
  useEffect(() => {
    async function fetchCurrentUserData() {
      if (!auth.currentUser) return;

      try {
        const userRef = doc(db, 'users', auth.currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setCurrentUserData(userSnap.data());
        }
      } catch (error) {
        console.error('Error fetching current user data:', error);
      }
    }
    fetchCurrentUserData();
  }, [auth.currentUser]);

  // Check username availability with debounce
  useEffect(() => {
    if (!changeDetail || !formData.username) {
      setUsernameAvailability(null);
      return;
    }

    const debounce = setTimeout(async () => {
      setIsUsernameChecking(true);
      try {
        const usernameQuery = query(
          collection(db, 'users'),
          where('username', '==', formData.username)
        );
        const querySnapshot = await getDocs(usernameQuery);

        if (querySnapshot.empty) {
          setUsernameAvailability({ available: true, message: 'This username is available' });
        } else {
          const userDoc = querySnapshot.docs[0];
          if (userDoc.id === auth.currentUser.uid) {
            setUsernameAvailability({ available: true, message: 'This username is available' });
          } else {
            setUsernameAvailability({ available: false, message: 'This username is not available!' });
          }
        }
      } catch (error) {
        console.error('Error checking username availability:', error);
        setUsernameAvailability(null);
      } finally {
        setIsUsernameChecking(false);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(debounce);
  }, [formData.username, changeDetail, auth.currentUser]);

  // Helper function to create a notification
  const createNotification = async (type, recipientId, message, targetId = null) => {
    if (!auth.currentUser || auth.currentUser.uid === recipientId) return; // Don't notify self

    try {
      const notifRef = collection(db, `notifications/${recipientId}/userNotifications`);
      await addDoc(notifRef, {
        type,
        senderId: auth.currentUser.uid,
        targetId,
        message,
        timestamp: serverTimestamp(),
        isRead: false
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  };

  // Notify subscribers when a new status is posted (example implementation)
  const notifySubscribersOnStatusPost = async (statusId, statusContent) => {
    if (!userData.notificationSubscribers || userData.notificationSubscribers.length === 0) return;

    const message = `${userData.name} posted a new status: "${statusContent.substring(0, 50)}${statusContent.length > 50 ? '...' : ''}"`;
    
    const notificationPromises = userData.notificationSubscribers.map((subscriberId) =>
      createNotification('status', subscriberId, message, statusId)
    );

    await Promise.all(notificationPromises);
  };

  // Fetch content based on active tab
  useEffect(() => {
    async function fetchContent() {
      const uid = userId || auth.currentUser?.uid;
      if (!uid) {
        toast.error('User ID not found');
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let contentData = { 
          movies: [], 
          status: [],
          music: [],
          favorites: { movies: [], status: [] }, 
          liked: { movies: [], status: [] } 
        };

        // Fetch Movies
        if (activeTab === 'movies') {
          const moviesQuery = query(
            collection(db, "movies"),
            where("userRef", "==", uid),
            orderBy("timestamp", "desc")
          );
          const moviesSnap = await getDocs(moviesQuery);
          contentData.movies = moviesSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));
        }

        // Fetch Status
        if (activeTab === 'status') {
          const statusQuery = query(
            collection(db, "status"),
            where("userRef", "==", uid),
            orderBy("timestamp", "desc")
          );
          const statusSnap = await getDocs(statusQuery);
          contentData.status = statusSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));
        }

        // Fetch Music
        if (activeTab === 'music') {
          const musicQuery = query(
            collection(db, "music"),
            where("userRef", "==", uid),
            orderBy("timestamp", "desc")
          );
          const musicSnap = await getDocs(musicQuery);
          contentData.music = musicSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));
        }

        // Fetch Favorites (movies and status)
        if (activeTab === 'favorites') {
          // Fetch saved movies
          const favoritesMoviesQuery = query(
            collection(db, "movies"),
            where("savedBy", "array-contains", uid),
            orderBy("timestamp", "desc")
          );
          const favoritesMoviesSnap = await getDocs(favoritesMoviesQuery);
          contentData.favorites.movies = favoritesMoviesSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));

          // Fetch saved status
          const favoritesStatusQuery = query(
            collection(db, "status"),
            where("savedBy", "array-contains", uid),
            orderBy("timestamp", "desc")
          );
          const favoritesStatusSnap = await getDocs(favoritesStatusQuery);
          contentData.favorites.status = favoritesStatusSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));
        }

        // Fetch Liked (movies and status)
        if (activeTab === 'liked') {
          // Fetch liked movies
          const likedMoviesQuery = query(
            collection(db, "movies"),
            where("likedBy", "array-contains", uid),
            orderBy("timestamp", "desc")
          );
          const likedMoviesSnap = await getDocs(likedMoviesQuery);
          contentData.liked.movies = likedMoviesSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));

          // Fetch liked status
          const likedStatusQuery = query(
            collection(db, "status"),
            where("likedBy", "array-contains", uid),
            orderBy("timestamp", "desc")
          );
          const likedStatusSnap = await getDocs(likedStatusQuery);
          contentData.liked.status = likedStatusSnap.docs.map(doc => ({
            id: doc.id,
            data: doc.data()
          }));
        }

        setContent(contentData);
      } catch (error) {
        toast.error('Failed to load content');
        console.error("Error fetching content:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchContent();
  }, [activeTab, userId, auth.currentUser]);

  // Reset subTab when switching main tabs
  useEffect(() => {
    if (activeTab === 'favorites' || activeTab === 'liked') {
      setSubTab('movies');
    }
  }, [activeTab]);

  function onLogout() {
    auth.signOut();
    navigate('/');
  }

  function onChange(e) {
    let value = e.target.value;
    
    if (e.target.id === 'username') {
      value = value.toLowerCase()
        .replace(/\s+/g, '')
        .replace(/[^a-z0-9.]/g, '');
    }

    setFormData(prev => ({
      ...prev,
      [e.target.id]: value
    }));
  }

  async function uploadImage(file, type) {
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should not exceed 10MB');
      return;
    }

    setIsImageUpdating(prev => ({ ...prev, [type === 'profileImages' ? 'profile' : 'cover']: true }));

    const storage = getStorage();
    const user = auth.currentUser;
    const storageRef = ref(storage, `${type}/${user.uid}`);
    
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      await updateProfile(user, { 
        [type === 'profileImages' ? 'photoURL' : 'coverImage']: downloadURL 
      });
      
      await updateDoc(doc(db, 'users', user.uid), {
        [type === 'profileImages' ? 'photoURL' : 'coverImage']: downloadURL
      });

      setUserData(prev => ({
        ...prev,
        [type === 'profileImages' ? 'profileImage' : 'coverImage']: downloadURL
      }));

      toast.success(`${type === 'profileImages' ? 'Profile' : 'Cover'} image updated successfully`);
    } catch (error) {
      toast.error(`Failed to update ${type === 'profileImages' ? 'profile' : 'cover'} image`);
    } finally {
      setIsImageUpdating(prev => ({ ...prev, [type === 'profileImages' ? 'profile' : 'cover']: false }));
    }
  }

  const handleProfileImageClick = () => {
    profileImageInputRef.current.click();
  };

  const handleCoverImageClick = () => {
    coverImageInputRef.current.click();
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadImage(file, 'profileImages');
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      uploadImage(file, 'coverImages');
    }
  };

  async function deleteProfileImage() {
    setIsImageUpdating(prev => ({ ...prev, profile: true }));
    try {
      const user = auth.currentUser;
      await updateProfile(user, { photoURL: null });
      await updateDoc(doc(db, 'users', user.uid), { photoURL: null });
      setUserData(prev => ({
        ...prev,
        profileImage: defaultAvatar
      }));
      toast.success('Profile image deleted successfully');
    } catch (error) {
      toast.error('Failed to delete profile image');
    } finally {
      setIsImageUpdating(prev => ({ ...prev, profile: false }));
      setShowProfileOptions(false);
    }
  }

  async function deleteCoverImage() {
    setIsImageUpdating(prev => ({ ...prev, cover: true }));
    try {
      const user = auth.currentUser;
      await updateDoc(doc(db, 'users', user.uid), { coverImage: null });
      setUserData(prev => ({
        ...prev,
        coverImage: defaultCover
      }));
      toast.success('Cover image deleted successfully');
    } catch (error) {
      toast.error('Failed to delete cover image');
    } finally {
      setIsImageUpdating(prev => ({ ...prev, cover: false }));
      setShowCoverOptions(false);
    }
  }

  async function onSubmit() {
    try {
      if (formData.name.length < 3) {
        toast.error('Name must be at least 3 characters long');
        return;
      }

      if (!usernameAvailability?.available) {
        toast.error('Please choose a different username');
        return;
      }

      await updateProfile(auth.currentUser, {
        displayName: formData.name
      });

      const userRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userRef, {
        name: formData.name,
        username: formData.username,
        bio: formData.bio
      });

      setUserData(prev => ({
        ...prev,
        name: formData.name,
        username: formData.username,
        bio: formData.bio
      }));

      toast.success('Profile updated successfully');
      setChangeDetail(false);
    } catch (error) {
      toast.error('Failed to update profile');
    }
  }

  async function handleEditClick() {
    if (changeDetail) {
      await onSubmit();
    } else {
      setChangeDetail(true);
    }
  }

  async function onDelete(contentId, contentType) {
    if (window.confirm(`Are you sure you want to delete this ${contentType}?`)) {
      try {
        await deleteDoc(doc(db, contentType, contentId));
        setContent(prev => {
          if (activeTab === 'favorites' || activeTab === 'liked') {
            return {
              ...prev,
              [activeTab]: {
                ...prev[activeTab],
                [subTab]: prev[activeTab][subTab].filter(item => item.id !== contentId)
              }
            };
          } else {
            return {
              ...prev,
              [activeTab]: prev[activeTab].filter(item => item.id !== contentId)
            };
          }
        });
        toast.success('Deleted successfully');
      } catch (error) {
        toast.error('Failed to delete');
      }
    }
  }

  function onEdit(contentId) {
    if (activeTab === 'movies') {
      navigate(`/edit-movie/${contentId}`);
    } else if (activeTab === 'music') {
      navigate(`/edit-music/${contentId}`);
    } else {
      navigate(`/edit-${activeTab}/${contentId}`);
    }
  }

  // Handle status view
  const handleStatusView = async (statusId) => {
    if (!auth.currentUser) return;
    
    try {
      setActiveStatusId(statusId);
      await markStatusAsViewed(statusId);
    } catch (error) {
      console.error('Error handling status view:', error);
    } finally {
      setActiveStatusId(null);
    }
  };

  // Handle follow/unfollow
  const handleFollow = async () => {
    if (!auth.currentUser) {
      navigate('/sign-in');
      return;
    }
  
    setIsFollowLoading(true);
    try {
      const wasFollowing = isFollowing(userId);
      await toggleFollow(userId);
      
      // Update followers count in local state
      setUserData(prev => {
        const newCount = isFollowing(userId) ? prev.followers - 1 : prev.followers + 1;
        return {
          ...prev,
          followers: Math.max(0, newCount) // Ensure count doesn't go negative
        };
      });

      if (!wasFollowing) {
        // Notify only on follow, not unfollow
        const message = `started following you.`;
        await createNotification('follow', userId, message);
      } else {
        // If unfollowing, remove from notification subscribers
        if (isSubscribedToNotifications) {
          await handleNotifyToggle();
        }
      }
    } catch (error) {
      toast.error('Failed to update follow status');
    } finally {
      setIsFollowLoading(false);
    }
  };

  // Handle notification toggle
  const handleNotifyToggle = async () => {
    if (!auth.currentUser || isCurrentUser) return;

    setIsNotifyLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      const currentUserId = auth.currentUser.uid;

      if (isSubscribedToNotifications) {
        // Unsubscribe: Remove current user from notificationSubscribers
        await updateDoc(userRef, {
          notificationSubscribers: arrayRemove(currentUserId)
        });
        toast.success('Notifications turned off');
      } else {
        // Subscribe: Add current user to notificationSubscribers
        await updateDoc(userRef, {
          notificationSubscribers: arrayUnion(currentUserId)
        });
        toast.success('Notifications turned on');
      }
    } catch (error) {
      console.error('Error toggling notifications:', error);
      toast.error('Failed to update notification settings');
    } finally {
      setIsNotifyLoading(false);
    }
  };

  // Handle click on followers count
  const handleFollowersClick = () => {
    navigate(`/profile/${userId}/followers`);
  };

  // Handle click on following count
  const handleFollowingClick = () => {
    navigate(`/profile/${userId}/following`);
  };

  // Handle share profile
  const handleShare = async () => {
    // Construct the shareable URL for the profile
    const shareUrl = `${window.location.origin}/profile/${userId || auth.currentUser.uid}`;
    const shareData = {
      title: `${userData.name}'s Profile`,
      text: `Check out ${userData.name}'s profile on this platform!`,
      url: shareUrl,
    };

    try {
      // Check if the Web Share API is supported
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        // Fallback: Copy the URL to clipboard if Web Share API is not supported
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Profile link copied to clipboard! Share it manually.');
      }
    } catch (error) {
      console.error('Error sharing profile:', error);
      // Fallback in case of error: Copy the URL to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.info('Profile link copied to clipboard! Share it manually.');
      } catch (clipboardError) {
        console.error('Error copying to clipboard:', clipboardError);
        toast.error('Failed to share profile. Please share the link manually.');
      }
    }
  };

  // Start a new chat or navigate to an existing one
  const startChat = () => {
    toast.info('This feature is not yet available in your region!', {
      position: "top-center",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };
  

  // Determine follow button text and style
  const getFollowButtonState = () => {
    if (!isInitialized) {
      return {
        text: '...',
        className: 'bg-gray-500 text-white',
        icon: <FaUserPlus className="mr-2" />,
        disabled: true
      };
    }
    
    if (isFollowing(userId)) {
      return {
        text: 'Following',
        className: 'bg-black text-white hover:bg-gray-800',
        icon: <FaUserCheck className="mr-2" />,
        disabled: false
      };
    }
    if (shouldFollowBack(userId)) {
      return {
        text: 'Follow Back',
        className: 'bg-blue-500 text-white hover:bg-blue-600',
        icon: <FaUserPlus className="mr-2" />,
        disabled: false
      };
    }
    return {
      text: 'Follow',
      className: 'bg-white text-black hover:bg-gray-100',
      icon: <FaUserPlus className="mr-2" />,
      disabled: false
    };
  };

  const followState = getFollowButtonState();

  const renderActionButton = () => {
    if (isCurrentUser) {
      return (
        <div className="flex items-center space-x-4">
          <div className="relative" ref={createOptionsRef}>
            <button 
              onClick={() => setShowCreateOptions(!showCreateOptions)}
              className="flex items-center bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-full transition"
            >
              <FaPlus className="mr-2" />
              Create
            </button>
            
            {showCreateOptions && (
              <div className="absolute z-10 mt-2 w-48 bg-gray-800 rounded-md shadow-lg">
                <div className="py-1">
                  <button
                    onClick={() => {
                      navigate("/upload-status");
                      setShowCreateOptions(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                  >
                    Post Status
                  </button>
                  {auth.currentUser?.email === "wanderaemmanuelwere@gmail.com" && (
                    <button
                      onClick={() => {
                        navigate("/upload-movie");
                        setShowCreateOptions(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                    >
                      Upload Movie
                    </button>
                  )}
                  {auth.currentUser?.email === "wanderaemmanuelwere@gmail.com" && (
                    <button
                      onClick={() => {
                        navigate("/upload-music");
                        setShowCreateOptions(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700"
                    >
                      Upload Music
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <button
            onClick={handleEditClick}
            disabled={changeDetail && (isUsernameChecking || (usernameAvailability && !usernameAvailability.available))}
            className={`flex items-center bg-gray-700 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-full transition ${changeDetail && (isUsernameChecking || (usernameAvailability && !usernameAvailability.available)) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <FaEdit className="mr-2" />
            {changeDetail ? 'Save Changes' : 'Edit profile'}
          </button>
          
          <button
            onClick={onLogout}
            className="text-white font-medium hover:text-red-400 transition"
          >
            Sign out
          </button>
        </div>
      );
    } else {
      return (
        <div className="action-buttons">
          <button 
            onClick={handleFollow}
            disabled={isFollowLoading}
            className={`px-4 py-2 rounded-full font-medium transition flex items-center ${followState.className} ${isFollowLoading ? 'opacity-70' : ''}`}
          >
            {followState.icon}
            {isFollowing(userId) ? 'Following' : shouldFollowBack(userId) ? 'Follow Back' : 'Follow'}
          </button>
          <button 
            onClick={handleNotifyToggle}
            disabled={isNotifyLoading || !isFollowing(userId)}
            className={`bg-gray-700 text-white font-medium py-2 px-6 rounded-full hover:bg-gray-600 transition flex items-center ${isNotifyLoading || !isFollowing(userId) ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <FaBell className="mr-2" />
            {isNotifyLoading ? '...' : (isSubscribedToNotifications ? 'Turn Off' : 'Notify')}
          </button>
            <FaEnvelope size={23}  onClick={startChat} className="mr-2 text-white" />
          
        </div>
      );
    }
  };

  const renderEmptyState = () => {
    const messages = {
      movies: "No movies uploaded yet",
      status: "No status posts yet",
      music: "No music uploaded yet",
      favorites: `No ${subTab} saved yet`,
      liked: `No ${subTab} liked yet`
    };

    const actions = {
      movies: {
        text: "Upload Your First Movie",
        action: () => navigate("/upload-movie"),
        condition: isCurrentUser && auth.currentUser?.email === "wanderaemmanuelwere@gmail.com"
      },
      status: {
        text: "Post Your First Status",
        action: () => navigate("/upload-status"),
        condition: isCurrentUser
      },
      music: {
        text: "Upload Your First Music",
        action: () => navigate("/upload-music"),
        condition: isCurrentUser && auth.currentUser?.email === "wanderaemmanuelwere@gmail.com"
      }
    };

    return (
      <div className="flex flex-col items-center justify-center py-16 text-gray-300">
        <p className="text-lg">{messages[activeTab]}</p>
        {actions[activeTab]?.condition && (
          <button 
            onClick={actions[activeTab].action}
            className="mt-4 bg-blue-500 text-white px-4 py-2 rounded-md hover:bg-blue-400 transition"
          >
            {actions[activeTab].text}
          </button>
        )}
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <Spinner />;
    }

    const items = activeTab === 'favorites' || activeTab === 'liked' ? content[activeTab][subTab] : content[activeTab];

    if (items.length === 0) {
      return renderEmptyState();
    }

    if (activeTab === 'movies' || (activeTab === 'favorites' && subTab === 'movies') || (activeTab === 'liked' && subTab === 'movies')) {
      return (
        <div className="movie-grid">
          {items.map(item => (
            <div key={item.id} className="movie-item-wrapper">
              <MovieItem movie={{ ...item.data, id: item.id }} />
              {isCurrentUser && activeTab === 'movies' && (
                <div className="movie-item-actions">
                  <button onClick={() => onEdit(item.id)} title="Edit">
                    <FaEdit size={14} />
                  </button>
                  <button onClick={() => onDelete(item.id, 'movies')} title="Delete">
                    <FaTrash size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    } else if (activeTab === 'status' || (activeTab === 'favorites' && subTab === 'status') || (activeTab === 'liked' && subTab === 'status')) {
      return (
        <div className="space-y-4">
          {items.map(item => (
            <div key={item.id} className="status-item-wrapper">
              <StatusPostItem 
                status={{ ...item.data, id: item.id }} 
                onView={() => handleStatusView(item.id)}
                isActive={activeStatusId === item.id}
              />
              {isCurrentUser && activeTab === 'status' && (
                <div className="status-item-actions">
                  <button 
                    onClick={() => onDelete(item.id, 'status')} 
                    title="Delete"
                  >
                    <FaTrash size={14} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      );
    } else if (activeTab === 'music') {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map(item => (
            <div key={item.id} className="relative">
              <MusicPlayer 
                music={{ ...item.data, id: item.id }} 
                onEdit={() => onEdit(item.id)}
                onDelete={() => onDelete(item.id, 'music')}
                showActions={isCurrentUser && activeTab === 'music'}
              />
            </div>
          ))}
        </div>
      );
    }
  };

  if (!isInitialized) {
    return <Spinner />;
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Cover Image */}
      <div className="relative h-48 w-full bg-gray-800 overflow-hidden">
        <img 
          src={userData.coverImage} 
          alt="Cover" 
          className="w-full h-full object-cover"
        />
        <div className="absolute top-4 right-4 flex items-center space-x-2">
          <button 
            onClick={handleShare}
            className="more-button"
          >
            <FaShare size={16} />
          </button>
          {isCurrentUser && (
            <>
              <button 
                onClick={() => setShowCoverOptions(!showCoverOptions)}
                className="more-button"
                disabled={isImageUpdating.cover}
              >
                {isImageUpdating.cover ? (
                  <Spinner size="sm" />
                ) : (
                  <FiMoreVertical size={16} />
                )}
              </button>
              {showCoverOptions && (
                <div ref={coverOptionsRef} className="dropdown-menu" style={{ top: '40px', right: '0' }}>
                  <button onClick={handleCoverImageClick}>
                    <FaEdit size={14} />
                    Change Cover Image
                  </button>
                  <button 
                    onClick={deleteCoverImage}
                    className={userData.coverImage === defaultCover ? 'disabled' : ''}
                    disabled={userData.coverImage === defaultCover}
                  >
                    <FaTrash size={14} />
                    Delete Cover Image
                  </button>
                </div>
              )}
              <input
                type="file"
                ref={coverImageInputRef}
                onChange={handleCoverImageChange}
                accept="image/*"
                className="hidden"
              />
            </>
          )}
        </div>
      </div>

      {/* Profile Info Section */}
      <div className="px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col sm:flex-row items-start sm:items-end relative">
          {/* Profile Image */}
          <div className="relative -mt-16 sm:-mt-20">
            <img 
              src={userData.profileImage} 
              alt="Profile" 
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-gray-700 object-cover"
            />
            {isCurrentUser && (
              <div className="absolute bottom-0 right-0">
                <button 
                  onClick={() => setShowProfileOptions(!showProfileOptions)}
                  className="more-button"
                  disabled={isImageUpdating.profile}
                >
                  {isImageUpdating.profile ? (
                    <Spinner size="sm" />
                  ) : (
                    <FiMoreHorizontal size={16} />
                  )}
                </button>
                {showProfileOptions && (
                  <div ref={profileOptionsRef} className="dropdown-menu" style={{ bottom: '40px', right: '0' }}>
                    <button onClick={handleProfileImageClick}>
                      <FaEdit size={14} />
                      Change Profile Image
                    </button>
                    <button 
                      onClick={deleteProfileImage}
                      className={userData.profileImage === defaultAvatar ? 'disabled' : ''}
                      disabled={userData.profileImage === defaultAvatar}
                    >
                      <FaTrash size={14} />
                      Delete Profile Image
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  ref={profileImageInputRef}
                  onChange={handleProfileImageChange}
                  accept="image/*"
                  className="hidden"
                />
              </div>
            )}
          </div>

          {/* Profile Details */}
          <div className="mt-4 sm:mt-0 sm:ml-6 flex-1 bg-gray-800 p-4 rounded-lg shadow-sm -mt-4 sm:mt-0">
            {changeDetail ? (
              <div className="space-y-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                    Nickname
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={onChange}
                    minLength={3}
                    className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-900 text-white"
                  />
                </div>
                <div>
                  <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    id="username"
                    value={formData.username}
                    onChange={onChange}
                    className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-900 text-white"
                  />
                  {usernameAvailability && (
                    <p className={`text-sm mt-1 ${usernameAvailability.available ? 'text-blue-300' : 'text-red-400'}`}>
                      {usernameAvailability.message}
                    </p>
                  )}
                </div>
                <div>
                  <label htmlFor="bio" className="block text-sm font-medium text-gray-300 mb-1">
                    Bio
                  </label>
                  <textarea
                    id="bio"
                    value={formData.bio}
                    onChange={onChange}
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-600 rounded-md bg-gray-900 text-white"
                  />
                </div>
              </div>
            ) : (
              <div>
                <h1 className="text-2xl font-bold text-white inline-flex items-center">
                  {userData.name}
                  <VerificationBadge isVerified={userData.verified} />
                </h1>
                <p className="text-gray-400">@{userData.username}</p>
                <p className="mt-2 text-gray-200">{userData.bio}</p>
                <div className="flex items-center mt-2 text-gray-400">
                  <span 
                    onClick={handleFollowersClick}
                    className="mr-4 clickable-count"
                  >
                    {userData.followers} followers
                  </span>
                  <span 
                    onClick={handleFollowingClick}
                    className="clickable-count"
                  >
                    {userData.following} following
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-4 sm:mt-0 sm:ml-4">
            {renderActionButton()}
          </div>
        </div>

        {/* Account Type - Hidden for non-current users */}
        {isCurrentUser && (
          <div className="mt-6">
            <p className="text-sm font-medium text-gray-300">Account type:</p>
            <div className="inline-block bg-blue-700 text-white px-3 py-1 rounded-md text-sm mt-1">
              {userData.userCategory}
            </div>
            <p className="mt-1 text-sm text-gray-400">
              Enjoy personalized content based on your account type.
            </p>
          </div>
        )}

        {/* Tabs */}
        <div className="mt-8 border-b border-gray-700 sticky-tabs">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('status')}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'status' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'}`}
            >
              Status
            </button>
            {isCurrentUser && (
              <>
                {auth.currentUser?.email === "wanderaemmanuelwere@gmail.com" && (
                  <button
                    onClick={() => setActiveTab('movies')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'movies' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'}`}
                  >
                    Movies
                  </button>
                )}
                {auth.currentUser?.email === "wanderaemmanuelwere@gmail.com" && (
                  <button
                    onClick={() => setActiveTab('music')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'music' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'}`}
                  >
                    Music
                  </button>
                )}
                <button
                    onClick={() => setActiveTab('favorites')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'favorites' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'}`}
                  >
                    Favorites
                  </button>
                  <button
                    onClick={() => setActiveTab('liked')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'liked' ? 'border-blue-500 text-blue-400' : 'border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-600'}`}
                  >
                    Liked
                  </button>
              </>
            )}
          </nav>
        </div>

        {/* Sub-Tabs for Favorites and Liked */}
        {(activeTab === 'favorites' || activeTab === 'liked') && isCurrentUser && (
          <div className="mt-4 flex space-x-4">
            <button
              onClick={() => setSubTab('movies')}
              className={`sub-tab ${subTab === 'movies' ? 'active' : ''}`}
            >
              Movies
            </button>
            <button
              onClick={() => setSubTab('status')}
              className={`sub-tab ${subTab === 'status' ? 'active' : ''}`}
            >
              Status
            </button>
          </div>
        )}

        {/* Content Section */}
        <main className="min-h-screen py-6 px-2 sm:px-4">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}